import type { JpoolScore } from '~/utils/api'
import { defineStore } from 'pinia'
import { JSTAKING_VALIDATOR_VOTE, VALIDATORS_RELOAD_INTERVAL } from '~/config'
import { useConnectionStore } from '~/store/connection'
import { useEpochStore } from '~/store/epoch'
import { getValidators, getValidatorsInfo } from '~/utils/api'

export const useValidatorsStore = defineStore('validators', () => {
  const connectionStore = useConnectionStore()
  const stakePoolAddress = computed(() => connectionStore.stakePoolAddress)

  const loading = ref(false)
  const jstakingValidator = useLocalStorage<Record<string, any>>('cached-jstaking-validator', {})

  const epochStore = useEpochStore()
  const epochNumber = computed(() => epochStore.epochNumber)

  async function load() {
    if (loading.value && !epochNumber.value) {
      return
    }

    try {
      loading.value = true
      const cluster = connectionStore.cluster
      const network = cluster.replace('-beta', '')
      const [validatorsStats, validatorsInfo] = await Promise.all([
        getValidators(network),
        getValidatorsInfo(),
      ])

      if (cluster === connectionStore.cluster) {
        for (const item of validatorsStats) {
          if (item.voteId !== JSTAKING_VALIDATOR_VOTE) {
            continue
          }
          const info = validatorsInfo?.find && validatorsInfo?.find(v => v.vote_account === item.voteId)
          const pubKey = item.validatorId

          const apy = info?.apy ? info.apy * 100 : 0
          const jitoApy = info?.jito_apy ? info?.jito_apy * 100 : 0

          jstakingValidator.value = {
            id: pubKey,
            fee: item.fee,
            voteId: item.voteId,
            apy,
            jitoApy,
            apyTotal: apy + jitoApy,
            isDelinquent: item.isDelinquent,
            lastVote: item.lastVote,
            svName: item.svName,
            totalStake: Number(item.totalStake),
            name: item.name ?? '',
            details: item.details,
            website: item.website,
            keybaseUsername: item.keybaseUsername,
            iconUrl: item.iconUrl,
            image: item.keybaseUsername
              ? `https://keybase.io/${item.keybaseUsername}/picture`
              : undefined,
            url: `https://www.validators.app/validators/${network}/${pubKey}`,
          }
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  watch([stakePoolAddress, epochNumber], useDebounceFn(() => {
    if (epochNumber.value > 0) {
      load().then()
    }
  }, 500), { immediate: true })

  if (!import.meta.env.SSR) {
    setInterval(load, VALIDATORS_RELOAD_INTERVAL)
  }

  return {
    loading,
    jstakingValidator,
    load,
  }
})

export type ValidatorData = {
  id: string
  fee: number
  apy: number
  jitoApy: number
  apyTotal: number
  voteId: string
  jpoolLamports: number
  totalStake: number
  isDelinquent: boolean
  lastVote: string
  inJpool: boolean
  inTop33: boolean
  svName: string
  // withdrawStake: number
  name: string
  details: string | undefined
  website: string | undefined
  keybaseUsername: string | undefined
  iconUrl: string | undefined
  url: string | undefined
  image: string | undefined
  jpoolScore: number | undefined
  jpoolRank: number | undefined
  svtMemberScore: number | undefined
  desiredStake: number | undefined
  dropReason?: string
  allScores?: JpoolScore
  dataCenter: string
  asnStakePercent: number
  countryStakePercent: number
  cityStakePercent: number
  dcStakePercent: number
  dcCity: string
  dcCountry: string
  nodeVersion: string
  aso: string
  countryName: string
  ip: string
  leaderSlotsTotal: number
  leaderSlotsDone: number
  leaderSlotsEpoch: number
}

export type ValidatorDataExtended = ValidatorData & {
  solStakedNum: number
  jsolStakedNum: number
}
